import { MarketVacancy, MarketVacancyCondition } from '../interfaces/market-vacancy';

export interface ConditionChip { text: string; eis: boolean; years?: number; quote?: string; notChecked: boolean; }
export interface ConditionRow { key: 'experience' | 'hardskills' | 'certifications' | 'other' | 'softskills'; label: string; chips: ConditionChip[]; }
export interface ConditionView { state: 'wishes' | 'full'; rows: ConditionRow[]; hideYearsString: boolean; incomplete: boolean; }

const chip = (c: MarketVacancyCondition, extra: Partial<ConditionChip> = {}): ConditionChip =>
  ({ text: c.label, eis: c.strength === 'eis', quote: c.quote, notChecked: false, ...extra });

/** Null = unprocessed or empty: the page renders as before conditions existed. */
export function buildConditionView(v: MarketVacancy): ConditionView | null {
  if (!v.conditions_extracted_at || !v.conditions?.length) {
    return null;
  }
  const conds = [...(v.conditions ?? [])].sort((a, b) => a.position - b.position);
  const of = (kind: MarketVacancyCondition['kind']) => conds.filter(c => c.kind === kind);

  const experience = of('experience').map(c => chip(c, { years: c.min_years ?? undefined }));
  const certConds = of('certification');
  const named = new Set(certConds.map(c => c.Certification_id).filter(id => id !== null));
  const certs = [
    ...certConds.map(c => chip(c)),
    ...(v.certifications ?? []).filter(c => !named.has(c.id)).map(c => ({ text: c.name, eis: false, notChecked: false })),
  ];
  const other = of('other').map(c => chip(c, { notChecked: c.strength === 'eis' }));
  const outlined = (list: { name: string }[] | undefined) => (list ?? []).map(s => ({ text: s.name, eis: false, notChecked: false }));

  const rows: ConditionRow[] = [
    { key: 'experience', label: 'vacancy.conditions_row_experience', chips: experience },
    { key: 'hardskills', label: 'vacancy.conditions_row_hardskills', chips: outlined(v.skills) },
    { key: 'certifications', label: 'vacancy.conditions_row_certifications', chips: certs },
    { key: 'other', label: 'vacancy.conditions_other', chips: other },
    { key: 'softskills', label: 'vacancy.conditions_row_softskills', chips: outlined(v.developments) },
  ];
  return {
    state: conds.some(c => c.strength === 'eis') ? 'full' : 'wishes',
    rows: rows.filter(r => r.chips.length > 0),
    hideYearsString: experience.length > 0,
    incomplete: !!v.conditions_incomplete,
  };
}
